import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { motion } from 'framer-motion';
import { formatDeductionRange } from '../config';

const SectionLabelRow = ({ label, accent }) => (
  <TableRow>
    <TableCell colSpan={8} sx={{ py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: accent }}>
        {label}
      </Typography>
    </TableCell>
  </TableRow>
);

const DeductionsEmployeesTable = ({
  activeDepartment,
  employeeRows,
  paginatedRows,
  totalPages,
  page,
  rowsPerPage,
  searchTerm,
  isMobile,
  currencyFormatter,
  statusPalette,
  onSearchTermChange,
  onRowsPerPageChange,
  onPageChange,
  onViewEmployee,
  onExportEmployeeDeductions,
  onAddEmployeeDeduction,
  onEditEmployee
}) => {
  const sectionLabels = {
    current: 'Current deductions',
    available: 'Employees without deductions',
    history: 'History'
  };

  const groupedRows = paginatedRows.reduce((accumulator, row, index) => {
    const previousRow = paginatedRows[index - 1];
    if (!previousRow || previousRow.section !== row.section) {
      accumulator.push({
        type: 'section',
        key: `section-${row.section}-${index}`,
        label: sectionLabels[row.section] || 'Section'
      });
    }

    accumulator.push({
      type: 'employee',
      key: row.id || `${row.section}-${row.employeeId}-${index}`,
      row,
      isRepeatedEmployee:
        Boolean(previousRow) &&
        previousRow.section === row.section &&
        previousRow.employeeId === row.employeeId
    });
    return accumulator;
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.22 }}
    >
      <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
                placeholder="Search employees or deductions in this store"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: '#6b7280' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="deduction-employee-rows-label">Rows</InputLabel>
                <Select
                  labelId="deduction-employee-rows-label"
                  value={rowsPerPage}
                  label="Rows"
                  onChange={(event) => onRowsPerPageChange(Number(event.target.value))}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Chip
                  label={`${employeeRows.length} visible rows`}
                  sx={{ bgcolor: `${activeDepartment.accent}14`, color: activeDepartment.accent, fontWeight: 700 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#ffffff' }}>
                <TableCell sx={{ fontWeight: 700 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Deduction setup</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{activeDepartment.typeLabel}</TableCell>
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Frequency</TableCell>}
                {!isMobile && (
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Amount
                  </TableCell>
                )}
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>}
                {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Deduction range</TableCell>}
                <TableCell sx={{ fontWeight: 700 }} align="right">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isMobile ? 4 : 8} sx={{ py: 8, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                      No employees match the current filter.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      Try a different search term to find employees in this store.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {groupedRows.map((item) => {
                if (item.type === 'section') {
                  return <SectionLabelRow key={item.key} label={item.label} accent={activeDepartment.accent} />;
                }

                const { row, isRepeatedEmployee } = item;
                return (
                  <TableRow key={item.key} hover>
                    <TableCell>
                      {!isRepeatedEmployee && (
                        <>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
                            {row.employeeName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {row.employeeId}
                          </Typography>
                          {row.hasDeduction && onExportEmployeeDeductions && (
                            <Button
                              size="small"
                              startIcon={<PictureAsPdfOutlinedIcon fontSize="small" />}
                              onClick={() => onExportEmployeeDeductions(row)}
                              sx={{
                                display: 'flex',
                                mt: 0.75,
                                p: 0,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                                color: activeDepartment.accent,
                                fontSize: 12,
                                fontWeight: 700,
                                lineHeight: 1.2,
                                textTransform: 'none',
                                '& .MuiButton-startIcon': {
                                  mr: 0.5
                                }
                              }}
                            >
                              Export deductions
                            </Button>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          row.section === 'history'
                            ? 'History'
                            : row.hasDeduction
                              ? 'Has deduction'
                              : 'No deduction'
                        }
                        color={row.section === 'current' ? 'success' : 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                      {row.hasDeduction && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 0.75 }}>
                          {statusPalette[row.status]?.label || row.status}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{row.hasDeduction ? row.deductionType : '-'}</TableCell>
                    {!isMobile && <TableCell>{row.hasDeduction ? row.frequency : '-'}</TableCell>}
                    {!isMobile && (
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        {row.hasDeduction
                          ? currencyFormatter.format(row.frequency === 'One time' ? row.totalAmount || row.amount : row.amount)
                          : '-'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        {row.hasDeduction
                          ? row.frequency === 'One time'
                            ? '-'
                            : `${row.paidInstallments}/${row.totalInstallments}`
                          : '-'}
                      </TableCell>
                    )}
                    {!isMobile && (
                      <TableCell>
                        {row.hasDeduction ? formatDeductionRange(row.firstDeductionDate, row.lastDeductionDate) : '-'}
                      </TableCell>
                    )}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        {row.hasDeduction && (
                          <Tooltip title="View deduction progress">
                            <IconButton size="small" onClick={() => onViewEmployee(row)}>
                              <VisibilityOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.section !== 'history' && (
                          <Tooltip title={row.hasDeduction ? 'Add another deduction' : 'Set deduction'}>
                            <IconButton size="small" onClick={() => onAddEmployeeDeduction(row)}>
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.section === 'current' && (
                          <Tooltip title="Edit deduction">
                            <IconButton size="small" onClick={() => onEditEmployee(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            p: 2.5,
            borderTop: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
            Showing {paginatedRows.length} of {employeeRows.length} rows
          </Typography>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_event, nextPage) => onPageChange(nextPage)}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: activeDepartment.accent,
                color: 'white',
                '&:hover': {
                  backgroundColor: activeDepartment.accent
                }
              }
            }}
          />
        </Box>
      </Paper>
    </motion.div>
  );
};

export default DeductionsEmployeesTable;
